import { useEffect } from "react";
import { motion } from "framer-motion";
import { EASE } from "./Reveal";

export default function ProjectModal({ project, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="project-modal"
      className="fixed inset-0 z-[90]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div
        data-testid="project-modal-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-espresso/70 backdrop-blur-sm"
      />
      <motion.aside
        className="absolute right-0 top-0 h-full w-full max-w-2xl bg-ivory overflow-y-auto shadow-2xl"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.65, ease: EASE }}
      >
        <button
          data-testid="project-modal-close"
          onClick={onClose}
          aria-label="Close project"
          className="fixed top-5 right-5 z-10 grid h-11 w-11 place-items-center rounded-full bg-ivory/90 text-ink border border-bronze/20 hover:bg-ink hover:text-ivory transition-colors duration-300"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>

        <div className="h-72 sm:h-96 overflow-hidden">
          <img
            src={project.cover}
            alt={project.title}
            className="h-full w-full object-cover"
          />
        </div>

        <div className="p-7 sm:p-10">
          <p className="overline-label">
            {project.category} · {project.year}
          </p>
          <h2 className="mt-3 font-serif font-light text-3xl sm:text-5xl text-ink">
            {project.title}
          </h2>

          <dl className="mt-8 grid grid-cols-3 gap-4 border-y border-bronze/15 py-5">
            {[
              ["Location", project.location],
              ["Area", project.area],
              ["Scope", project.scope],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500">
                  {k}
                </dt>
                <dd className="mt-1 text-[13px] sm:text-sm text-ink font-medium leading-snug">
                  {v}
                </dd>
              </div>
            ))}
          </dl>

          <p className="mt-7 text-[15px] sm:text-base font-light leading-relaxed text-stone-warm">
            {project.narrative}
          </p>

          {project.images[1] && (
            <div className="mt-8 overflow-hidden">
              <img
                src={project.images[1]}
                alt={`${project.title} detail`}
                loading="lazy"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          )}
        </div>
      </motion.aside>
    </motion.div>
  );
}
